import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate } from 'react-router-dom';
import {
  TVehiculeCheckSchema,
  vehiculeCheckSchema,
} from '@/schemas/vehicule-check/vehicule-check.schema';
import { VehiculeCheckRequestManagement } from '../components/VehiculeCheckRequestManagement';
import { useCreateVehiculeCheck } from '@/hooks/vehicule-check.hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionUserType,
  VehiculeCheckProgressStatus,
} from '@/api/entities/vehicule-check';
import { TVehiculeCheckEmailSchema } from '@/schemas/vehicule-check-email/vehicule-check-email.schema';
import { useState } from 'react';
import { useCreateVehiculeCheckEmail } from '@/hooks/vehicule-check-email.hooks';
import Loader from '@/components/Loading/Loader';

export const CreateVehiculeCheckRequestPage = () => {
  const { t } = useTranslation('inspection');
  const navigate = useNavigate();
  const [emailDetails, setEmailDetails] = useState<TVehiculeCheckEmailSchema>();

  const {
    watch: watchVehiculeCheck,
    control: vehiculeCheckControl,
    register: registerVehiculeCheck,
    setValue: setVehiculeCheckValue,
    setError: setVehiculeCheckError,
    handleSubmit: handleSubmitVehiculeCheck,
    formState: {
      errors: vehiculeCheckErrors,
      isSubmitting: isSubmittingVehiculeCheck,
    },
  } = useForm<TVehiculeCheckSchema>({
    resolver: zodResolver(vehiculeCheckSchema),
  });

  const onCreateEmail = (vehiculeCheckEmail: TVehiculeCheckEmailSchema) => {
    setEmailDetails(vehiculeCheckEmail);
  };

  const {
    mutate: mutateCreateVehiculeCheckEmail,
    isPending: isPendingCreateVehiculeCheckEmail,
  } = useCreateVehiculeCheckEmail({
    t,
    handleSuccess: () => navigate('/vehicule-checks'),
  });

  const {
    mutate: mutateCreateVehiculeCheck,
    isPending: isPendingCreateVehiculeCheck,
  } = useCreateVehiculeCheck({
    t,
    handleSuccess: (data) => {
      if (emailDetails?.emailSubject && emailDetails?.emailContent) {
        mutateCreateVehiculeCheckEmail({
          ...emailDetails,
          vehiculeCheckId: data?.['data']?.id,
        });
      } else {
        navigate('/vehicule-checks');
      }
    },
  });

  const onSubmitVehiculeCheck = async (vehiculeCheck: TVehiculeCheckSchema) => {
    vehiculeCheck = {
      ...vehiculeCheck,
      actionUserType: ActionUserType.INDIVIDUAL,
      progressStatus:
        emailDetails?.emailSubject && emailDetails?.emailContent
          ? VehiculeCheckProgressStatus.REQUEST_CREATED_AND_SENT
          : VehiculeCheckProgressStatus.REQUEST_CREATED,
    };
    mutateCreateVehiculeCheck(vehiculeCheck);
  };

  return (
    <>
      <PageHeader
        backToLink={history?.length > 1 ? -1 : '/vehicule-checks'}
        title={t('common:requestAnInspection')}
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem
              text={t('common:inspectionsList')}
              linkTo="/vehicule-checks"
            />
            <BreadcrumbItem text={t('common:inspection')} active />
          </BreadcrumbList>
        }
      />
      <div>
        <VehiculeCheckRequestManagement<TVehiculeCheckSchema>
          t={t}
          watch={watchVehiculeCheck}
          control={vehiculeCheckControl}
          register={registerVehiculeCheck}
          setValue={setVehiculeCheckValue}
          setError={setVehiculeCheckError}
          errors={vehiculeCheckErrors}
          submitDisabled={
            isSubmittingVehiculeCheck ||
            isPendingCreateVehiculeCheck ||
            isPendingCreateVehiculeCheckEmail
          }
          onSubmit={handleSubmitVehiculeCheck(onSubmitVehiculeCheck)}
          onCreateEmail={onCreateEmail}
        />
      </div>
      {(isSubmittingVehiculeCheck ||
        isPendingCreateVehiculeCheck ||
        isPendingCreateVehiculeCheckEmail) && <Loader />}
    </>
  );
};
